import { useEffect, useState } from "react";
import { CalendarCheck, Flame } from "lucide-react";

/**
 * Urgency bar — стимулирует действие "сейчас":
 * запись сегодня → выезд замерщика завтра. Показывает кол-во свободных слотов
 * на завтрашний день. Слоты считаются стабильно по дате (детерминированно).
 */

function getRemainingSlots(): number {
  const now = new Date();
  // Стабильный псевдо-рандом по завтрашней дате — число не прыгает при перезагрузке
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  const day = tomorrow.getDate() + tomorrow.getMonth() * 31;
  const seed = (day * 9301 + 49297) % 233280;
  const variance = Math.floor((seed / 233280) * 3); // 0..2
  // К вечеру слотов на завтра остаётся меньше
  const hour = now.getHours();
  const base = hour >= 18 ? 3 : hour >= 14 ? 4 : 5;
  let remaining = base - variance;
  if (remaining < 2) remaining = 2;
  if (remaining > 5) remaining = 5;
  return remaining;
}

function formatTomorrow(): string {
  const t = new Date(Date.now() + 24 * 60 * 60 * 1000);
  return t.toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
}

interface UrgencyBarProps {
  onBook?: () => void;
}

const UrgencyBar = ({ onBook }: UrgencyBarProps = {}) => {
  const [slots, setSlots] = useState<number>(3);
  const [tomorrow, setTomorrow] = useState<string>("");

  useEffect(() => {
    setSlots(getRemainingSlots());
    setTomorrow(formatTomorrow());
  }, []);

  return (
    <div className="px-4 md:px-0">
      <div className="rounded-2xl border border-primary/30 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-3.5 md:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5">
        <div className="flex items-center gap-2.5 flex-1">
          <div className="h-9 w-9 md:h-10 md:w-10 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0">
            <CalendarCheck className="h-4.5 w-4.5 md:h-5 md:w-5" strokeWidth={2.2} />
          </div>
          <div>
            <div className="text-[13px] md:text-[14px] font-bold text-foreground leading-tight">
              Запишитесь сегодня — приедем уже завтра{tomorrow ? ` (${tomorrow})` : ""}
            </div>
            <div className="text-[11px] md:text-[12px] text-muted-foreground mt-0.5">
              Бесплатный замер с каталогом 120+ тканей · расчёт за 1 день
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 self-stretch sm:self-auto">
          <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-destructive/10 text-destructive text-[12px] md:text-[13px] font-bold whitespace-nowrap">
            <Flame className="h-4 w-4 animate-pulse" />
            Свободно {slots} {slots === 1 ? "слот" : slots < 5 ? "слота" : "слотов"} на завтра
          </div>
          {onBook && (
            <button
              onClick={onBook}
              className="hidden sm:inline-flex items-center justify-center rounded-lg bg-primary text-primary-foreground px-3.5 py-2 text-[12px] md:text-[13px] font-semibold transition-all active:scale-[0.97] hover:shadow-md hover:shadow-primary/30 whitespace-nowrap"
            >
              Записаться на замер
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UrgencyBar;
